import React from "react";
import "./BlankScreen.css";
import CurrentTimeBar from "./CurrentTimeBar";
import { formatDurationLabel } from "../../utils/formatDuration";

interface BlankScreenProps {
  message?: string;
  hijriDate?: string;
  gregorianDate?: string;
  screenData?: any;
  prayerTimes?: any;
  tomorrowPrayerTimes?: any;
  showDateTime?: boolean;
  backgroundUrl?: string;
  countdownSecondsLeft?: number;
  countdownTotalSeconds?: number;
  countdownPrayer?: string | null;
}

const BlankScreen: React.FC<BlankScreenProps> = ({
  message = "",
  hijriDate,
  gregorianDate,
  screenData,
  prayerTimes,
  tomorrowPrayerTimes,
  showDateTime = true,
  backgroundUrl,
  countdownSecondsLeft = 0,
  countdownTotalSeconds = 0,
  countdownPrayer,
}) => {
  const hasBackground = !!backgroundUrl;

  // --- Countdown display ---
  const showCountdown =
    !!screenData?.blankScreenShowCountdown && countdownSecondsLeft > 0;

  const countdownFormat: "digital" | "short" | "medium" | "long" =
    screenData?.blankScreenCountdownFormat &&
    ["digital", "short", "medium", "long"].includes(
      screenData.blankScreenCountdownFormat,
    )
      ? screenData.blankScreenCountdownFormat
      : "digital";

  const countdownDirection: "down" | "up" =
    screenData?.blankScreenCountdownDirection === "up" ? "up" : "down";

  const totalSeconds = Math.max(0, countdownTotalSeconds);
  const remainingSeconds = Math.max(0, countdownSecondsLeft);
  const elapsedSeconds =
    totalSeconds > 0
      ? Math.min(totalSeconds, Math.max(0, totalSeconds - remainingSeconds))
      : 0;

  const countdownDisplaySeconds =
    countdownDirection === "up" && totalSeconds > 0
      ? elapsedSeconds
      : remainingSeconds;

  const countdownLabel = showCountdown
    ? formatDurationLabel(countdownDisplaySeconds, countdownFormat)
    : "";

  const cdX = screenData?.blankScreenCountdownX ?? 50;
  const cdY = screenData?.blankScreenCountdownY ?? 50;

  const countdownStyle: React.CSSProperties = {
    position: "absolute",
    left: `${cdX}%`,
    top: `${cdY}%`,
    transform: "translate(-50%, -50%)",
    "--bsc-color": screenData?.blankScreenCountdownColor || "#FFFFFF",
    "--bsc-scale": `${(screenData?.blankScreenCountdownFontScalePercent ?? 100) / 100}`,
    "--bsc-weight": screenData?.blankScreenCountdownFontWeight ?? 700,
  } as React.CSSProperties;

  const msgX = screenData?.blankScreenMessageX ?? 50;
  const msgY = screenData?.blankScreenMessageY ?? 50;

  // --- Message styling ---
  const messageStyle: React.CSSProperties = {
    position: "absolute",
    left: `${msgX}%`,
    top: `${msgY}%`,
    transform: "translate(-50%, -50%)",
    color: screenData?.blankScreenMessageColor || "#FFC107",
    fontSize: `${((screenData?.blankScreenMessageFontScalePercent ?? 100) / 100) * 6}em`,
  };

  return (
    <div
      className={`blank-screen ${hasBackground ? "blank-screen--has-bg" : ""}`}
    >
      {/* Full-screen background image (when set) */}
      {hasBackground && (
        <img
          src={backgroundUrl}
          alt=""
          className="blank-screen-bg-image"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      )}

      {/* Pass the pre-calculated dates to CurrentTimeBar */}
      {showDateTime && (
        <div
          className={`blank-screen-timebar ${hasBackground ? "blank-screen-timebar--transparent" : ""}`}
        >
          <CurrentTimeBar
            screenData={screenData}
            prayerTimes={prayerTimes}
            tomorrowPrayerTimes={tomorrowPrayerTimes}
            overrideIslamicDate={hijriDate}
            overrideGregorianDate={gregorianDate}
          />
        </div>
      )}

      {/* Salah in progress message */}
      {message && (
        <div className="blank-screen-message" style={messageStyle}>
          {message}
        </div>
      )}

      {/* Salah countdown */}
      {showCountdown && (
        <div className="blank-screen-countdown" style={countdownStyle}>
          <div className="bsc-countdown-value">{countdownLabel}</div>
        </div>
      )}
    </div>
  );
};

export default BlankScreen;
